import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, ElementRef, ViewChild, computed, effect, inject, signal, untracked } from '@angular/core';
import { Popover } from 'primeng/popover';
import { Router } from '@angular/router';
import { fromEvent } from 'rxjs';
import { AuthService } from '@app/services/auth.service';
import { TabService } from '@app/services/tab.service';
import { ChatTab } from '@app/services/types';
import { CloseTabButtonComponent } from '@app/chat/components/close-tab-button/close-tab-button.component';
import { TabButtonComponent } from '@app/chat/components/tab-button/tab-button.component';
import { OpenVehicleSelectionButtonComponent } from '@app/vehicle-selection/components/open-vehicle-selection-button.component';
import { TranslocoModule } from '@jsverse/transloco';

// Important note:
// This number should fit to the tab size in the template
const TAB_WIDTH = 230;

@Component({
  selector: 'app-tab-navigation',
  imports: [CommonModule, TranslocoModule, TabButtonComponent, OpenVehicleSelectionButtonComponent, CloseTabButtonComponent, Popover],
  templateUrl: './tab-navigation.component.html',
  styleUrl: './tab-navigation.component.scss',
})
export class TabNavigationComponent implements AfterViewInit {
  @ViewChild('tabNavigation') thisEl?: ElementRef;
  @ViewChild('overflowPopover') overflowPopover?: Popover;

  private readonly destroyRef = inject(DestroyRef);
  router = inject(Router);
  authService = inject(AuthService);
  tabService = inject(TabService);

  availableBarWidth = signal<number>(0);
  renderdTabs = signal<ChatTab[]>([]);

  isActive = computed(() => this.availableBarWidth() > 0);

  /**
   * Listen to changes in the server tabs.
   * They happen
   *  - after the inital tabs load,
   *  - after a tab has been added or removed and
   *  - when the active tab changes.
   */
  onServerTabsChange = effect(() => {
    if (!this.isActive()) {
      return;
    }

    const activeTab = untracked(() => this.tabService.activeTab());

    // If a tab is added or removed
    if (this._tabsHaveChanged()) {
      this.renderdTabs.set(this.tabService.tabs());

      // Redirect if the active tab is not in the rendered list anymore
      if (activeTab && !this.renderdTabs().includes(activeTab) && !this.tabService.tabsLoading()) {
        this.navigateToTab(undefined);
        return;
      }

      setTimeout(() => {
        this.availableBarWidth.set(this.thisEl?.nativeElement.offsetWidth);
      }, 10);
    }

    untracked(() => {
      this._putToFrontIfNecessary(activeTab);
    });
  });

  maxVisibleTabsCount = computed(() => {
    return Math.floor(this.availableBarWidth() / TAB_WIDTH);
  });

  visibleTabs = computed(() => {
    const tabs = this.renderdTabs();
    return tabs.slice(0, this.maxVisibleTabsCount() - 1);
  });

  overflowingTabs = computed(() => {
    const tabs = this.renderdTabs();
    return tabs.slice(this.maxVisibleTabsCount() - 1);
  });

  ngAfterViewInit() {
    if (this.thisEl) {
      this.availableBarWidth.set(this.thisEl.nativeElement.offsetWidth);
    }
    fromEvent(window, 'resize')
      // Throttling causes issues with the tab width calculation when resizing the window quickly
      // .pipe(throttleTime(THROTTLE_TIME), takeUntilDestroyed(this.destroyRef))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.availableBarWidth.set(this.thisEl?.nativeElement.offsetWidth);
      });
  }

  navigateToTab(tab: ChatTab | undefined): void {
    if (tab) this.router.navigate(['chat', tab._id]);
    else this.router.navigate(['chat']);
    this._putToFrontIfNecessary(tab);
    this.overflowPopover?.hide();
  }

  closeOverflowingTabs(): void {
    this.overflowPopover?.hide();
  }

  isLeftOfActiveTab(tabIndex: number): boolean {
    const activeTab = this.tabService.activeTab();
    if (!activeTab) return false;
    const nextTab = this.visibleTabs()[tabIndex + 1];
    if (!nextTab) return false;
    return nextTab._id === activeTab._id;
  }

  isNextToOverflowButton(tabIndex: number): boolean {
    const tabIsLastOne = tabIndex === this.visibleTabs().length - 1;
    const hasOverflowingTabs = this.overflowingTabs().length > 0;
    return tabIsLastOne && hasOverflowingTabs;
  }

  onTabClose() {
    this.router.navigate(['chat']);
  }

  private _tabsHaveChanged() {
    const renderedTabs = untracked(() => this.renderdTabs());

    if (this.tabService.tabs()?.length !== renderedTabs.length) {
      return true;
    }
    const sortedServerTabs = [...this.tabService.tabs()].sort((a, b) => a._id.localeCompare(b._id));
    const sortedRenderedTabs = [...renderedTabs].sort((a, b) => a._id.localeCompare(b._id));
    return sortedServerTabs.some((tab, index) => tab._id !== sortedRenderedTabs[index]._id);
  }

  private _putToFrontIfNecessary(tab: ChatTab | undefined): void {
    if (tab && this._isInvisibleTab(tab)) {
      const newList = [...this.renderdTabs()];
      const tabIndex = newList.indexOf(tab);
      newList.splice(tabIndex, 1);
      newList.unshift(tab);
      this.renderdTabs.set(newList);
    }
  }

  private _isInvisibleTab(tab: ChatTab | undefined): boolean {
    if (!tab) return false;
    return this.overflowingTabs().includes(tab);
  }
}
